import get from 'lodash/get';

import { post } from 'Api';
import store from 'Redux/store';
import { refreshAuth, setNewsletterStatus } from 'Redux/ducks/auth';
import {
  SUBSCRIBED as STATUS_SUBSCRIBED,
  PENDING as STATUS_PENDING,
  UNSUBSCRIBED as STATUS_UNSUBSCRIBED,
  NONE as STATUS_NONE,
} from 'Helpers/CRM/email-subscribe-status';
import { recordSubscribe } from 'Helpers/CRM/subscribe-suppression';
import State from 'Helpers/State';

export const EMAIL_INITIAL = 'frontend/email-subscribe/EMAIL_INITIAL';
export const EMAIL_SUBSCRIBE = 'frontend/email-subscribe/EMAIL_SUBSCRIBE';
export const EMAIL_ERROR = 'frontend/email-subscribe/EMAIL_ERROR';

export const SUBSCRIBED = 'subscribed';
export const OPT_IN_REQUIRED = 'opt_in_required';
export const NO_CHANGE = 'no_change';

const API_STATUSES = [SUBSCRIBED, OPT_IN_REQUIRED, NO_CHANGE];

export const initialState = {
  currentState: EMAIL_INITIAL,
  status: '',
  emailResponse: '',
  errorText: '',
  email: '',
  context: '',
};

const startResetCounter = () => {
  const displayTime = 1000 * 5;
  window.setTimeout(() => {
    store.dispatch({ type: EMAIL_INITIAL, payload: '' });
  }, displayTime);
};

/**
 * @param {String}  email
 * @param {String}  context       subscribedFrom context this placement posts with
 * @param {Boolean} ignoreDouble  UNUSED - the API decides opt-in from authentication now and
 *                                ignores anything we send. Kept as a positional placeholder
 *                                because 22 call sites pass it; dropping it here would silently
 *                                shift `newsletter` into its place. Removed in its own change
 * @param {Boolean} newsletter    false unsubscribes locally, true posts a subscribe
 * @param {String}  country
 * @param {String}  competitionId
 */
export function postEmail(email, context, ignoreDouble, newsletter, country, competitionId = null) {
  const dataToSend = {
    email,
    subscribedFrom: context,
    newsletterOption: newsletter,
    competitionId,
  };
  if (country) {
    dataToSend.countryPassed = country;
  }

  return async (dispatch) => {
    if (newsletter === false) {
      if (get(store.getState(), 'auth.authenticated')) {
        // Local reset only - the server was never told, so do not assert `unsubscribed`
        setNewsletterStatus(STATUS_NONE);
      }
      return dispatch({ type: EMAIL_INITIAL, payload: '' });
    }

    if (newsletter) {
      localStorage.setItem('singleOptinConsentGiven', Date.now());
    }

    const { response, data } = await post(dispatch, '/crm/email-subscribe', dataToSend);

    let text;
    let reason;
    const state = store.getState();
    const pageId = State.getPageId();
    if (response.ok) {
      const returnMessage = get(data, 'message', '');
      const isStatusMessage = API_STATUSES.includes(returnMessage);
      const status = get(data, 'status') || (isStatusMessage ? returnMessage : '');

      text = 'Thank you for subscribing';
      if (status === OPT_IN_REQUIRED) {
        text = 'Check your inbox to confirm your newsletter subscription';
      } else if (returnMessage !== '' && !isStatusMessage) {
        text = returnMessage;
      }

      if (state.auth && state.auth.authenticated) {
        if (status === OPT_IN_REQUIRED) {
          setNewsletterStatus(STATUS_PENDING);
        } else if (status !== NO_CHANGE) {
          setNewsletterStatus(STATUS_SUBSCRIBED);
        }
      }

      if (newsletter && status !== NO_CHANGE) {
        recordSubscribe();
      }

      startResetCounter();

      if (!newsletter || status === NO_CHANGE) {
        return dispatch({ type: EMAIL_INITIAL, payload: '' });
      }

      return dispatch({
        type: EMAIL_SUBSCRIBE,
        payload: { text, status, context, pageId, email },
      });
    }

    switch (response.status) {
      case 400:
        text = 'Invalid email address. Please try again.';
        reason = 'Invalid email address';
        startResetCounter();
        return dispatch({ type: EMAIL_ERROR, payload: { text, context, pageId, email, reason } });

      case 500:
        text = 'Unable to subscribe. Please try again.';
        reason = 'Unable to subscribe';
        startResetCounter();
        return dispatch({ type: EMAIL_ERROR, payload: { text, context, pageId, email, reason } });

      default:
        text = 'An error occured. Please try again.';
        reason = 'Error occured';
        startResetCounter();
        return dispatch({ type: EMAIL_ERROR, payload: { text, context, pageId, email, reason } });
    }
  };
}

export async function optinConfirm(token) {
  const { response } = await post(store.dispatch, '/crm/front/double-optin', { token, url: window.location.href });
  if (response.ok) {
    setNewsletterStatus(STATUS_SUBSCRIBED);
    store.dispatch(refreshAuth());
    return null;
  }

  throw new Error('Optin Request Failed');
}

export function unsubscribe() {
  return async (dispatch) => {
    const { response } = await post(dispatch, '/crm/frontunsubscribe', {});
    if (response.ok) {
      setNewsletterStatus(STATUS_UNSUBSCRIBED);
    }
  };
}

export function reset() {
  store.dispatch({ type: EMAIL_INITIAL, payload: '' });
}
